// Cache comparison helpers for incremental DOM rebuild start index.
// Example: return first changed source position or -1 when unchanged.
import type { BuildSourceEntry, CachedSourceEntry } from './types'
import type { LuisCompileContext } from '../context'
import { areCompileContextsEqual } from './cache-context'

// Return first index where source identity/signature diverges from previous build.
/** @internal Return first index where source identity/signature diverges from previous build. */
export function findFirstChangedIndex(previous: CachedSourceEntry[], current: BuildSourceEntry[]): number {
  const compareLength = Math.min(previous.length, current.length)

  // Compare shared prefix entries and stop at first mismatch.
  for (let index = 0; index < compareLength; index += 1) {
    const previousEntry = previous[index]
    const currentEntry = current[index]
    // Detect any identity/content mismatch at this position.
    if (
      previousEntry._sourceId !== currentEntry.sourceId
      || previousEntry._kind !== currentEntry.kind
      || previousEntry._signature !== currentEntry.signature
    ) {
      return index
    } else {
      // Continue comparing unchanged prefix entries.
    }
  }

  // Mark change when list length differs after identical shared prefix.
  if (previous.length !== current.length) {
    return compareLength
  } else {
    // Return -1 when cache and current source list are identical.
    return -1
  }
}

// Check whether rebuild can reuse untouched tail entries after current index.
/** @internal Check whether rebuild can reuse untouched tail entries after current index. */
export function canReuseUnchangedTail(
  previous: CachedSourceEntry[],
  current: BuildSourceEntry[],
  currentIndex: number,
  currentContext: LuisCompileContext,
): boolean {
  const previousCurrent = previous[currentIndex]
  // Stop reuse when previous cache has no entry at current position.
  if (!previousCurrent) {
    return false
  } else {
    // Continue reuse checks with context and tail signature validation.
  }

  // Stop reuse when context diverged from previous run at this position.
  if (!areCompileContextsEqual(previousCurrent._contextAfter, currentContext)) {
    return false
  } else {
    // Continue tail-entry identity/signature validation.
  }

  // Stop reuse when current list length differs from previous list length.
  if (current.length !== previous.length) {
    return false
  } else {
    // Continue validating unchanged tail entries.
  }

  // Validate that every tail entry remains identity/signature-stable.
  for (let index = currentIndex + 1; index < current.length; index += 1) {
    const previousEntry = previous[index]
    const currentEntry = current[index]
    // Stop reuse when any tail entry differs from previous snapshot.
    if (!previousEntry || !isEntryIdentityStable(previousEntry, currentEntry)) {
      return false
    } else {
      // Continue scanning remaining tail entries.
    }
  }

  // Return true when context and tail entries are fully reusable.
  return true
}

// Compare build entry identity/signature against cached entry snapshot.
function isEntryIdentityStable(previous: CachedSourceEntry, current: BuildSourceEntry): boolean {
  // Return false when source identity/content signature changed.
  if (
    previous._sourceId !== current.sourceId
    || previous._kind !== current.kind
    || previous._signature !== current.signature
  ) {
    return false
  } else {
    // Return true for stable identity/signature entries.
    return true
  }
}
